
import { useEffect, useMemo, useState } from 'react';
import { TriangleAlert } from 'lucide-react';

import type { ApiError } from '../../api/client';
import { AuthorFilter } from '../../components/AuthorFilter';
import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardHead,
  EmptyState,
  Pill,
  SearchableSelect,
  Seg,
  Slider,
  SkeletonRows,
  Textarea,
  useAudioPreview,
  type SearchableOption,
} from '../../components/ui';
import {
  useCurrentUser,
  useProviderVoices,
  useSynthesize,
  useTtsRun,
  useTtsRuns,
  useVoices,
} from '../../hooks';
import { languageLabel, languagesForModel, modelSupportsLanguage } from '../../lib/languages';
import type {
  Id,
  SharedVoice,
  TtsModel,
  TtsProvider,
  TtsSynthesizeRequest,
} from '../../types/api';
import {
  SPEED_MAX,
  SPEED_MIN,
  TTS_MODELS_BY_PROVIDER,
  TTS_MODEL_CARTESIA,
  TTS_MODEL_EXPRESSIVE,
  TTS_MODEL_TUNABLE,
} from '../../types/api';
import styles from './TtsPage.module.css';
import { DocxDrop } from './DocxDrop';
import { formatCount, formatElapsed } from './format';
import { useVoiceCatalog } from '../voices/catalogQueries';
import { TtsHistory } from './TtsHistory';
import { TtsResult } from './TtsResult';
import { VoiceLibraryPanel } from './VoiceLibraryPanel';
import { VoicePicker } from './VoicePicker';
import {
  buildVoiceOptions,
  catalogKey,
  countByGroup,
  filterVoices,
  fromCatalogEntry,
  fromLiveItem,
  fromSharedVoice,
  type CatalogSource,
  type VoiceGroup,
  type VoiceOption,
} from './voiceOptions';

const MAX_TEXT_CHARS = 5000;

function cleanText(text: string): string {
  return text.trim();
}

type VoiceTab = 'own' | 'library';

export function TtsPage() {
  const { can } = useCurrentUser();
  const canEdit = can('editor');

  const [text, setText] = useState('');
  const [language, setLanguage] = useState('ru');
  const [provider, setProvider] = useState<TtsProvider>('elevenlabs');
  const [model, setModel] = useState<TtsModel>(TTS_MODEL_EXPRESSIVE);
  const [speed, setSpeed] = useState(1.0);
  const [stability, setStability] = useState(0.35);
  const [similarity, setSimilarity] = useState(0.75);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState<VoiceGroup | 'all'>('all');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [voiceTab, setVoiceTab] = useState<VoiceTab>('own');
  const [openRunId, setOpenRunId] = useState<Id | null>(null);
  const [segmentTexts, setSegmentTexts] = useState<Record<string, string[]>>({});
  const [addedSources, setAddedSources] = useState<CatalogSource[]>([]);

  const audio = useAudioPreview();

  const catalog = useVoiceCatalog({ available: true, usable_only: true, limit: 200, provider });
  const catalogUseless = catalog.isError || (catalog.isSuccess && catalog.data.items.length === 0);
  const live = useProviderVoices({ enabled: catalogUseless });
  const library = useVoices({ limit: 200, provider });
  const [authorFilter, setAuthorFilter] = useState('');
  const runs = useTtsRuns({ limit: 30, author: authorFilter || undefined });
  const synth = useSynthesize();
  const run = useTtsRun(openRunId ?? undefined);

  const sources = useMemo(() => {
    const base = !catalogUseless && catalog.data
      ? catalog.data.items.map(fromCatalogEntry)
      : live.data
        ? live.data.map(fromLiveItem)
        : [];
    if (addedSources.length === 0) return base;
    const known = new Set(base.map((item) => item.voiceId));
    return [...base, ...addedSources.filter((item) => !known.has(item.voiceId))];
  }, [catalog.data, catalogUseless, live.data, addedSources]);

  const catalogLoaded = catalogUseless ? live.isSuccess : catalog.isSuccess;

  const options = useMemo(
    () =>
      buildVoiceOptions({
        sources,
        library: library.data?.items,
        catalogLoaded,
      }),
    [sources, library.data, catalogLoaded],
  );
  const counts = useMemo(() => countByGroup(options), [options]);
  const visible = useMemo(() => filterVoices(options, group, query), [options, group, query]);
  const selected = useMemo(
    () => options.find((option) => option.key === selectedKey) ?? null,
    [options, selectedKey],
  );

  const languageOptions = useMemo<SearchableOption[]>(
    () => languagesForModel(model).map((item) => ({ value: item.code, label: item.label })),
    [model],
  );

  useEffect(() => {
    if (!modelSupportsLanguage(model, language)) setLanguage('ru');
  }, [model, language]);

  const spoken = useMemo(() => cleanText(text), [text]);
  const chars = spoken.length;
  const overChars = chars > MAX_TEXT_CHARS;

  const busy = synth.isPending;
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!busy) {
      setElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsed(Math.round((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [busy]);

  useEffect(() => {
    if (!busy) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [busy]);

  const blockReason = ((): string | null => {
    if (!canEdit) return 'Роль «наблюдатель» не запускает озвучку.';
    if (busy) return 'Дождитесь конца озвучки.';
    if (!spoken) return 'Вставьте текст сценария.';
    if (overChars)
      return `Текст длиннее ${formatCount(MAX_TEXT_CHARS)} знаков (${formatCount(chars)}): озвучьте частями.`;
    if (!selected) return 'Выберите голос в списке справа.';
    if (!selected.usable)
      return `Голос «${selected.name}»: ${selected.problem ?? 'недоступен'}. Выберите другой.`;
    return null;
  })();

  const canSubmit = blockReason === null;

  const pickProvider = (next: TtsProvider) => {
    setProvider(next);
    const allowed = TTS_MODELS_BY_PROVIDER[next];
    if (!allowed.includes(model)) {
      setModel(next === 'cartesia' ? TTS_MODEL_CARTESIA : TTS_MODEL_EXPRESSIVE);
    }
  };

  const submit = () => {
    if (!canSubmit || !selected) return;
    const payload: TtsSynthesizeRequest = {
      language,
      provider,
      model_id: model,
      ...(provider === 'elevenlabs' && model === TTS_MODEL_TUNABLE
        ? { speed, stability, similarity_boost: similarity }
        : {}),
      title: spoken.slice(0, 120) || null,
      text: spoken,
      ...(selected.profileId
        ? { voice_profile_id: selected.profileId }
        : { voice_id: selected.voiceId }),
    };
    audio.stop();
    synth.mutate(payload, {
      onSuccess: (result) => {
        setSegmentTexts((current) => ({ ...current, [result.run_id]: [spoken] }));
        setOpenRunId(result.run_id);
      },
    });
  };

  const takeFromLibrary = (voice: SharedVoice, externalVoiceId: string) => {
    const source = fromSharedVoice(voice, externalVoiceId);
    setAddedSources((current) =>
      current.some((item) => item.voiceId === source.voiceId) ? current : [...current, source],
    );
    setGroup('all');
    setQuery('');
    setSelectedKey(catalogKey(source.voiceId));
  };

  const voiceNameOf = (voiceId: string): string => {
    const found = options.find((option) => option.voiceId === voiceId);
    return found ? found.name : voiceId.slice(0, 10);
  };

  const activeRun = run.data;
  const failure = describeSynthesisError(synth.error);

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <Card>
          <CardHead
            title={<span className="eyebrow">текст</span>}
            actions={
              <span className={styles.headActions}>
                <span className={styles.headLang}>
                  <SearchableSelect
                    fullWidth
                    options={languageOptions}
                    value={language}
                    onChange={(next) => setLanguage(next ?? language)}
                    disabled={busy}
                    ariaLabel="Язык текста"
                  />
                </span>
              </span>
            }
          />

          <Textarea
            className={styles.script}
            value={text}
            onChange={(event) => setText(event.target.value)}
            disabled={busy}
            spellCheck={false}
            aria-label="Текст сценария"
            placeholder="Вставьте текст сценария. Пустая строка делит его на части."
          />

          <DocxDrop onText={setText} disabled={busy || !canEdit} />

          <div className={styles.counterBar}>
            <span className={overChars ? styles.counterOver : styles.counter}>
              {formatCount(chars)} / {formatCount(MAX_TEXT_CHARS)}
            </span>
            <span className={styles.counterNote}>знаков</span>
            <span className={styles.counterRight}>
              {chars > 0 ? `${formatCount(chars)} знаков` : ''}
            </span>
          </div>

          <div className={styles.voiceTuning}>
            <div className={styles.tuningRow}>
              <span className={styles.tuningLabel}>Провайдер</span>
              <Seg<TtsProvider>
                ariaLabel="Провайдер озвучки"
                value={provider}
                onChange={pickProvider}
                options={[
                  { value: 'elevenlabs', label: 'ElevenLabs' },
                  { value: 'cartesia', label: 'Cartesia' },
                ]}
              />
            </div>

            {provider === 'elevenlabs' && (
              <div className={styles.tuningRow}>
                <span className={styles.tuningLabel}>Версия</span>
                <Seg<TtsModel>
                  ariaLabel="Версия ElevenLabs"
                  value={model}
                  onChange={setModel}
                  options={[
                    { value: TTS_MODEL_TUNABLE, label: 'v2' },
                    { value: TTS_MODEL_EXPRESSIVE, label: 'v3' },
                  ]}
                />
              </div>
            )}

            {provider === 'elevenlabs' && model === TTS_MODEL_TUNABLE && (
              <div className={styles.sliders}>
                <Slider
                  label="Скорость"
                  low="медленнее"
                  high="быстрее"
                  value={speed}
                  min={SPEED_MIN}
                  max={SPEED_MAX}
                  step={0.05}
                  disabled={busy}
                  onChange={setSpeed}
                />
                <Slider
                  label="Стабильность"
                  low="живее"
                  high="ровнее"
                  value={stability}
                  min={0}
                  max={1}
                  step={0.05}
                  disabled={busy}
                  onChange={setStability}
                />
                <Slider
                  label="Похожесть на образец"
                  low="ниже"
                  high="выше"
                  value={similarity}
                  min={0}
                  max={1}
                  step={0.05}
                  disabled={busy}
                  onChange={setSimilarity}
                />
              </div>
            )}
          </div>
        </Card>

        {busy && (
          <div className={styles.running} role="status" aria-live="polite">
            <div className={styles.runningHead}>
              <span className={styles.dot} />
              <span className={styles.runningTitle}>Идёт синтез, не закрывайте вкладку</span>
              <Pill>{formatCount(chars)} знаков</Pill>
              <span className={styles.elapsed}>идёт {formatElapsed(elapsed)}</span>
            </div>
            <div className={styles.stripe} />
            <div className={styles.runningNote}>
              Отменить уже нельзя. Ответ придёт целиком, ждём
              максимум 5 минут 30 секунд.
            </div>
          </div>
        )}

        {failure && !busy && (
          <div className={styles.errorCard} role="alert">
            <TriangleAlert size={16} strokeWidth={1.5} className={styles.errorIcon} />
            <div className={styles.errorBody}>
              <div className={styles.errorTitle}>{failure.title}</div>
              <div className={styles.errorCode}>{failure.detail}</div>
              <div className={styles.errorHint}>{failure.hint}</div>
              <div className={styles.errorActions}>
                <Button size="sm" onClick={() => synth.reset()}>
                  Скрыть
                </Button>
                {failure.canRetry && (
                  <Button size="sm" variant="primary" onClick={submit} disabled={!canSubmit}>
                    Повторить
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {openRunId && run.isPending && (
          <Card>
            <div className={styles.pad}>
              <SkeletonRows rows={2} />
            </div>
          </Card>
        )}
        {openRunId && run.error && (
          <ErrorBanner error={run.error} onRetry={() => void run.refetch()} />
        )}
        {activeRun && activeRun.segments.length > 0 && (
          <TtsResult
            run={activeRun}
            segmentTexts={segmentTexts[activeRun.run_id]}
            voiceLabel={voiceNameOf(activeRun.voice_id)}
            audio={audio}
          />
        )}
        {activeRun && activeRun.segments.length === 0 && (
          <Card>
            <EmptyState
              compact
              icon={<TriangleAlert size={17} strokeWidth={1.5} />}
              title="У прогона нет аудио"
              description="Озвучка не доделалась. Причина видна в истории ниже."
            />
          </Card>
        )}

        <div className={styles.footerBar}>
          <span className={styles.footerHint}>
            {blockReason ??
              `Голос: ${selected?.name ?? ''} · ${languageLabel(language)}`}
          </span>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {busy ? 'Синтезируется…' : activeRun ? 'Синтезировать заново' : 'Синтезировать'}
          </Button>
        </div>

        <TtsHistory
          filter={<AuthorFilter value={authorFilter} onChange={setAuthorFilter} />}
          data={runs.data}
          isPending={runs.isPending}
          error={runs.error}
          onRetry={() => void runs.refetch()}
          activeRunId={openRunId}
          onOpen={(item) => {
            audio.stop();
            setOpenRunId(item.run_id);
          }}
          voiceName={voiceNameOf}
        />
      </div>

      <div className={styles.side}>
        <Seg<VoiceTab>
          className={styles.voiceTabs}
          ariaLabel="Откуда брать голос"
          value={voiceTab}
          onChange={setVoiceTab}
          options={[
            { value: 'own', label: 'Наши' },
            { value: 'library', label: 'ElevenLabs' },
          ]}
        />

        {voiceTab === 'own' && (
        <VoicePicker
          options={options}
          visible={visible}
          counts={counts}
          group={group}
          onGroup={setGroup}
          query={query}
          onQuery={setQuery}
          selectedKey={selectedKey}
          onSelect={(option: VoiceOption) => setSelectedKey(option.key)}
          isLoading={(catalog.isPending || live.isFetching) && library.isPending}
          catalogError={catalogUseless ? (live.error ?? catalog.error) : null}
          fallbackToLive={catalogUseless && live.isSuccess}
          audio={audio}
        />
        )}

        {voiceTab === 'library' && (
          <VoiceLibraryPanel
            canAdd={canEdit}
            onAdded={takeFromLibrary}
            audio={audio}
            active
          />
        )}
      </div>
    </div>
  );
}

interface SynthesisFailure {
  title: string;
  detail: string;
  hint: string;
  canRetry: boolean;
}

function describeSynthesisError(error: ApiError | null): SynthesisFailure | null {
  if (!error) return null;
  const detail = error.errorId ? `${error.message} · error_id: ${error.errorId}` : error.message;

  if (error.status === 0) {
    return {
      title: 'Ответ не пришёл вовремя',
      detail,
      hint: 'Проверьте историю ниже: если озвучка там появилась, повторять не нужно.',
      canRetry: false,
    };
  }
  if (error.status === 503) {
    const missingKey = error.code === 'provider_not_configured';
    return {
      title: missingKey ? 'Озвучка не настроена' : 'Провайдер озвучки не отвечает',
      detail,
      hint: missingKey
        ? 'Обратитесь к администратору.'
        : 'Это не ваш ключ и не текст: сервис синтеза сейчас недоступен. Попробуйте через несколько минут.',
      canRetry: !missingKey,
    };
  }
  if (error.isRateLimited) {
    const seconds = error.retryAfterSeconds;
    return {
      title: 'Слишком часто',
      detail,
      hint: seconds
        ? `Подождите ${seconds} с и повторите.`
        : 'Слишком часто, подождите и повторите.',
      canRetry: true,
    };
  }
  if (error.isForbidden) {
    return {
      title: 'Недостаточно прав',
      detail,
      hint: 'Озвучивать может редактор и выше.',
      canRetry: false,
    };
  }
  if (error.code === 'voice_not_verified') {
    return {
      title: 'Голос не подтверждён',
      detail,
      hint: 'Повтор не поможет: пока права на голос не подтверждены в ElevenLabs, синтез им не пройдёт. Возьмите другой голос или переключитесь на Cartesia.',
      canRetry: false,
    };
  }
  if (error.status === 422 || error.status === 400) {
    return {
      title: 'Запрос отклонён до синтеза',
      detail,
      hint: 'Поправьте текст или голос и повторите.',
      canRetry: true,
    };
  }
  return {
    title: 'Синтез не удался',
    detail,
    hint: 'Проверьте историю ниже: если озвучка там появилась, повторять не нужно.',
    canRetry: true,
  };
}
